import * as React from "react";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { SvcResults, StudySvcRelevantParameters } from "../Shared/PFTAPITypes";
import { ToggleTestGraphVisibilityAction } from '../Shared/UseGraphs';
import { SvcGraphState } from './Graphing/UseSvcGraphs'
import { SVCResultParameters } from "./SVCResultsParameters";
import RepeatabilityTable from "../Shared/RepeatabilityTable";
import { SvcQualityAcceptabilityTable } from "./SvcQualityAcceptabilityTable";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from '../Shared/UseAcceptability';
import { SvcTestAcceptabilityTable } from "./SvcTestAcceptabilityTable";
import { NextGenFixedCommentsState } from "../Spirometry/Testing/UseNextGenFixedComments";
import { OverreadingResultsComments } from "../Shared/OverreadingResultsComments";
import { SvcSession } from "webportalclient";
import { ResultsContext } from "../Spirometry/UseResultsContext";

export interface SvcResultsTableProps {
    analysisSetId: string;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    siteAnalysisSetId: string;
    isViewOnly: boolean;
    resultData: SvcResults;
    session: SvcSession;
    fixedCommentsStateOverreading: NextGenFixedCommentsState;
    fixedCommentsDispatchOverreading;
    fixedCommentsStateTechnician: NextGenFixedCommentsState;
    fixedCommentsDispatchTechnician;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    graphState: SvcGraphState;
    technicianSessionCommentsAvailable: boolean;
    technicianTestCommentsAvailable: boolean;
    graphDispatch;
    studySvcRelevantParameters?: StudySvcRelevantParameters;
}

export const SvcResultsTable = (props: SvcResultsTableProps) => {
    if (props.resultData !== null) {
        return (
            <ResultsContext.Provider value={{ showBestValues: true, showPredictedValues: false, showPercentagePredicted: false, showReversibilityBaseValue: false, showReversibilityAbsoluteChange: false, showReversibilityPercentageChange: false }}>
                <div style={{ overflow: "scroll", maxWidth: "100%" }}>
                    <table id="ResultsTable" className="table table-striped">
                        <thead>
                            <tr>
                                <th />
                                {props.graphState.flow.Tests.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                    <th
                                        key={`TestHeader${t.TestId}`}
                                        style={{ background: t.ColorHex, color: "#fff" }}>
                                        {t.TestNumber}
                                    </th>
                                ))}
                                <th>Best</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Display</td>
                                {props.graphState.flow.Tests.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                    <td key={`TestVisibilityToggle${t.TestId}`}>
                                        <input
                                            checked={t.IsDisplayed}
                                            type="checkbox"
                                            data-automationid={`svc-results-table-toggle-test-${t.TestNumber}`}
                                            onChange={() =>
                                                props.graphDispatch(
                                                    ToggleTestGraphVisibilityAction.create(t.TestId)
                                                )
                                            }
                                        />
                                    </td>
                                ))}
                                <td />
                            </tr>
                            <tr>
                                <td>Time</td>
                                {props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                    <td key={`TestDateTime${t.TestId}`}>
                                        <LocalizedDateTime
                                            time={t.TestDateTime}
                                            accuracy={DateTimeAccuracy.Second}
                                            automationId={`test-datetime-${t.TestNumber}`}
                                        />
                                    </td>
                                ))}
                                <td />
                            </tr>

                            <SVCResultParameters
                                isLoading={props.acceptabilityState.loadingBestValues}
                                results={props.resultData}
                                isRejected={props.isRejected}
                                studySvcRelevantParameters={props.studySvcRelevantParameters}
                                showConfiguredParametersOnly={true}
                            />
                            <RepeatabilityTable
                                isLoading={props.acceptabilityState.loadingBestValues}
                                results={props.resultData}
                                isRejected={props.isRejected}
                                studyRelevantParameters={props.studySvcRelevantParameters}
                                showConfiguredParametersOnly={true}
                            />
                            <SvcQualityAcceptabilityTable results={props.resultData} />
                            <SvcTestAcceptabilityTable
                                isLoading={props.acceptabilityState.loadingBestValues}
                                isViewOnly={props.isViewOnly}
                                testResults={props.acceptabilityState.spirometryResults.TestResults}
                                displayTechnicianAcceptability={!props.isSiteAnalysisSet}
                                onAcceptabilityChange={(id, checked) =>
                                    props.acceptabilityDispatch(
                                        ToggleTestAcceptabilityAction.create(id, checked)
                                    )
                                }
                            />

                            <tr>
                                <td>Site Software Interpretation</td>
                                {props.resultData.TestResults.map(tr => {
                                    return <td key={`SoftwareInterpretation${tr.TestId}`}>N/A</td>;
                                })}
                                <td>{props.resultData.SoftwareInterpretation}</td>
                            </tr>

                            <OverreadingResultsComments
                                viewOnly={props.isViewOnly}
                                session={props.session}
                                showTechnician={true}
                                fixedCommentsDispatchTechnician={props.fixedCommentsDispatchTechnician}
                                fixedCommentsStateTechnician={props.fixedCommentsStateTechnician}
                                fixedCommentsDispatchOverreading={props.fixedCommentsDispatchOverreading}
                                fixedCommentsStateOverreading={props.fixedCommentsStateOverreading}
                            />
                        </tbody>
                    </table>
                </div>
            </ResultsContext.Provider>
        );
    } else {
        return <LoadingWithMessage message="Loading Spirometry Results..." />;
    }
}

export default SvcResultsTable;
